import { IconClose } from "@/assets/icons/geist/IconClose";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { IconPlus } from "@/assets/icons/geist/IconPlus";
import { Button } from "@/components/flexkit/Button";
import { NotAuthorized } from "@/components/NotAuthorized";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "@/components/podkit/dialog/Dialog";
import { EmptyStateCard } from "@/components/podkit/EmptyStateCard";
import { CopyableInput } from "@/components/podkit/forms/CopyableInput";
import { Input } from "@/components/podkit/forms/Input";
import { TagsInput } from "@/components/podkit/forms/TagsInput";
import { LoadingState } from "@/components/podkit/loading/LoadingState";
import { toast, useToast } from "@/components/podkit/toasts/use-toast";
import { Heading3, Heading4, Subheading } from "@/components/podkit/typography/Headings";
import { Text } from "@/components/podkit/typography/Text";
import { useDocumentTitle } from "@/hooks/use-document-title";
import { useMembership } from "@/hooks/use-membership";
import { TrackLocations, useSegmentTrack } from "@/hooks/use-segment";
import {
    useCreateOrganizationInvite,
    useListSSOConfigurations,
    useOrganization,
    useOrganizationInvite,
    useUpdateOrganization,
} from "@/queries/organization-queries";
import { joinOrganizationURL } from "@/routes/join-organization/join-organization";
import { CHANGE_PLAN_QUERY_KEY } from "@/routes/settings/organization/billing/Plan";
import { isValidDomain } from "@/utils/url-validation";
import { formatError } from "@/utils/errors";
import {
    OrganizationRole,
    OrganizationTier,
    SSOConfiguration_ProviderType,
    SSOConfigurationState,
    type SSOConfiguration,
} from "gitpod-next-api/gitpod/v1/organization_pb";
import { useCallback, useMemo, useState, type FC } from "react";
import { Link, useNavigate, useBlocker } from "react-router-dom";

export const InviteMembersPage: FC = () => {
    useDocumentTitle("Invite Members");
    const navigate = useNavigate();

    const { data: organization, isLoading: isLoadingOrganization } = useOrganization();
    const { membership, isPending: isLoadingMembership } = useMembership();

    // Lift email state to page level for navigation blocking
    const [emails, setEmails] = useState<string[]>([]);

    // Block navigation when there are unsent invites
    const blocker = useBlocker(() => emails.length > 0);

    const handleBlockerConfirmLeave = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.proceed();
        }
    }, [blocker]);

    const handleBlockerStay = useCallback(() => {
        if (blocker.state === "blocked") {
            blocker.reset();
        }
    }, [blocker]);

    const isFreePlan = organization?.tier === OrganizationTier.FREE_ONA || organization?.tier === OrganizationTier.FREE;

    if (!organization && isLoadingOrganization) {
        return <LoadingState />;
    }

    if (!membership && isLoadingMembership) {
        return <LoadingState />;
    }

    if (membership?.userRole !== OrganizationRole.ADMIN) {
        return (
            <NotAuthorized description="You do not have sufficient permissions to invite others to this organization. Contact your organization's admin to grant those permissions." />
        );
    }

    if (isFreePlan) {
        return (
            <EmptyStateCard
                title="Invite team members"
                description="Upgrade to Core for up to 100 team members, unlimited parallel environments, and more."
            >
                <Button
                    variant="primary"
                    onClick={() => navigate(`/settings/billing?${CHANGE_PLAN_QUERY_KEY}=true`)}
                    data-tracking-id="upgrade-invite-members-page"
                >
                    Upgrade
                </Button>
            </EmptyStateCard>
        );
    }

    return (
        <div className="flex flex-col gap-6 pt-6">
            <InviteMembers emails={emails} setEmails={setEmails} />
            <Text className="text-xs text-content-primary">
                If you would like to invite people by email domain, you can set that up in{" "}
                <Link className="font-bold underline" to="/settings/manage-organization">
                    Manage Organization
                </Link>
                .
            </Text>

            <UnsentInvitesDialog
                open={blocker.state === "blocked"}
                onOpenChange={handleBlockerStay}
                onConfirmLeave={handleBlockerConfirmLeave}
                emailCount={emails.length}
            />
        </div>
    );
};

const isCustom = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.CUSTOM;
const isBuiltin = (config: SSOConfiguration) => config.providerType === SSOConfiguration_ProviderType.BUILTIN;

type InviteMembersProps = {
    emails: string[];
    setEmails: React.Dispatch<React.SetStateAction<string[]>>;
};

export const InviteMembers: FC<InviteMembersProps> = ({ emails, setEmails }) => {
    const { data: ssoConfigs } = useListSSOConfigurations();
    const defaultConfigs = ssoConfigs?.filter(isBuiltin) || [];
    const customConfig = ssoConfigs ? ssoConfigs.find(isCustom) : undefined;

    const customSSOEnabled = customConfig?.state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE;
    const defaultSSOEnabled = defaultConfigs.some(
        (config) => config.state === SSOConfigurationState.SSO_CONFIGURATION_STATE_ACTIVE,
    );

    return (
        <div className="space-y-6">
            {customSSOEnabled && (
                <div className="flex max-w-[600px] items-center gap-2 rounded-md border border-border-light bg-surface-muted/75 p-3">
                    <IconInfo className="shrink-0" size="sm" />
                    <div>
                        <Text className="text-base font-bold">Your organization has SSO enabled</Text>
                        <Text className="text-base">
                            Log in user authentication is managed through your identity provider.
                        </Text>
                    </div>
                </div>
            )}
            <InviteMembersByLink />
            {defaultSSOEnabled && (
                <>
                    <InviteMembersByEmailAddress emails={emails} setEmails={setEmails} />
                    <InviteMembersByEmailDomain />
                </>
            )}
        </div>
    );
};

const InviteMembersByLink: FC = () => {
    const { data: invite } = useOrganizationInvite();
    const createOrganizationInvite = useCreateOrganizationInvite();

    const inviteURL = useMemo(() => {
        return joinOrganizationURL(invite?.inviteId || "");
    }, [invite?.inviteId]);

    return (
        <div className="space-y-3">
            <div>
                <Heading4>Invite link</Heading4>
                <Subheading>Share this link with others you&apos;d like to join your organization.</Subheading>
            </div>

            <div className="max-w-[600px]">
                <CopyableInput
                    value={inviteURL}
                    copyable
                    disabled
                    data-tracking-id={"invite-link-invite-members-by-link"}
                />
            </div>

            <div className="inline-flex items-start justify-start gap-3">
                <Button
                    variant="secondary"
                    onClick={() => {
                        createOrganizationInvite.mutate(void 0, {
                            onSuccess: () => {
                                toast({ title: `Invite link reset` });
                            },
                            onError: (error) => {
                                toast({ title: `Failed to reset invite link`, description: formatError(error) });
                            },
                        });
                    }}
                    data-tracking-id="reset-invite-link-invite-members-by-link"
                >
                    Reset Invite Link
                </Button>
            </div>
        </div>
    );
};

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type UnsentInvitesDialogProps = {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    onConfirmLeave: () => void;
    emailCount: number;
};

const UnsentInvitesDialog: FC<UnsentInvitesDialogProps> = ({ open, onOpenChange, onConfirmLeave, emailCount }) => {
    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-track-location={TrackLocations.InviteMembersUnsentInvitesDialog} className="w-96">
                <DialogHeader className="mb-0">
                    <DialogTitle>Unsent invites</DialogTitle>
                    <DialogDescription>
                        {`You have ${emailCount} unsent invite${emailCount !== 1 ? "s" : ""}. Click "Stay" to go back and send them, or discard to leave without sending.`}
                    </DialogDescription>
                </DialogHeader>
                <DialogFooter className="justify-end">
                    <DialogClose asChild>
                        <Button variant="outline" data-tracking-id="stay-unsent-invites">
                            Stay
                        </Button>
                    </DialogClose>
                    <Button variant="destructive" onClick={onConfirmLeave} data-tracking-id="discard-unsent-invites">
                        Discard invites
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};

type InviteMembersByEmailAddressProps = {
    emails: string[];
    setEmails: React.Dispatch<React.SetStateAction<string[]>>;
};

const InviteMembersByEmailAddress: FC<InviteMembersByEmailAddressProps> = ({ emails, setEmails }) => {
    const track = useSegmentTrack();
    const { data: invite } = useOrganizationInvite();
    const { data: organization } = useOrganization();
    const [inputValue, setInputValue] = useState("");
    const [inputError, setInputError] = useState("");

    const inviteURL = joinOrganizationURL(invite?.inviteId || "");
    const organizationName = organization?.name || "";

    const handleSendInvites = useCallback(() => {
        if (emails.length === 0 || !invite) return;

        emails.forEach((email) => {
            track("organization_email_invited", {
                email: email,
                invite_link: inviteURL,
                organization_name: organizationName,
            });
        });

        setEmails([]);
        toast({
            title: `Invitation${emails.length > 1 ? "s" : ""} sent successfully.`,
        });
    }, [emails, invite, inviteURL, organizationName, track, setEmails]);

    const validateEmail = useCallback(
        (email: string) => {
            if (emails.includes(email)) {
                return false;
            }
            return emailRegex.test(email);
        },
        [emails],
    );

    const handleAddEmail = useCallback(
        (e: React.FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            setInputError("");

            const email = inputValue.trim().toLowerCase();
            if (!email) return;

            if (!validateEmail(email)) {
                setInputError(emails.includes(email) ? "Email already added" : "Invalid email address");
                return;
            }

            setEmails((prev) => [...prev, email]);
            setInputValue("");
        },
        [inputValue, validateEmail, emails, setEmails],
    );

    const handleRemoveEmail = useCallback(
        (email: string) => {
            setEmails((prev) => prev.filter((e) => e !== email));
        },
        [setEmails],
    );

    return (
        <div className="space-y-3">
            <div>
                <Heading4>Invite by email</Heading4>
                <Subheading>Add email addresses, then send invitations</Subheading>
            </div>

            {/* Email input row */}
            <form onSubmit={handleAddEmail} className="flex max-w-[600px] gap-2">
                <div className="flex-1">
                    <Input
                        placeholder="email@example.com"
                        value={inputValue}
                        onChange={(e) => {
                            setInputError("");
                            setInputValue(e.target.value.toLowerCase());
                        }}
                        className={inputError ? "border-border-error" : ""}
                    />
                    {inputError && <Text className="mt-1 text-xs text-content-destructive">{inputError}</Text>}
                </div>
                <Button
                    type="submit"
                    variant="secondary"
                    disabled={!inputValue.trim()}
                    LeadingIcon={IconPlus}
                    data-tracking-id="add-email-invite-members"
                >
                    Add
                </Button>
            </form>

            {/* Pending invites section */}
            {emails.length > 0 && (
                <div className="max-w-[600px] rounded-lg border border-border-light bg-surface-secondary p-4">
                    <div className="mb-3 flex items-center justify-between">
                        <Text className="text-sm font-medium text-content-secondary">
                            {`Pending invite${emails.length !== 1 ? "s" : ""} (${emails.length})`}
                        </Text>
                    </div>
                    <div className="mb-4 flex flex-wrap gap-2">
                        {emails.map((email) => (
                            <div
                                key={email}
                                className="flex items-center gap-1 rounded-md border border-border-base bg-surface-primary px-2 py-1 text-sm"
                            >
                                <span>{email}</span>
                                <Button
                                    onClick={() => handleRemoveEmail(email)}
                                    type="button"
                                    variant="ghost"
                                    size="xs"
                                    className="h-5 w-5 p-0 hover:bg-surface-tertiary"
                                    LeadingIcon={IconClose}
                                    aria-label={`Remove ${email}`}
                                    data-tracking-id="remove-email-tag"
                                />
                            </div>
                        ))}
                    </div>
                    <Button
                        variant="primary"
                        onClick={handleSendInvites}
                        data-tracking-id="send-invite-invite-members-by-email-address"
                    >
                        {`Send ${emails.length} invite${emails.length !== 1 ? "s" : ""}`}
                    </Button>
                </div>
            )}
        </div>
    );
};

const InviteMembersByEmailDomain: FC = () => {
    const { toast } = useToast();

    const { data: orgData } = useOrganization();
    const inviteDomains = useMemo(() => orgData?.inviteDomains?.domains || [], [orgData]);
    const updateOrganization = useUpdateOrganization();

    const doUpdateInviteDomains = useCallback(
        async (inviteDomains: string[]) => {
            if (!orgData) {
                return;
            }

            try {
                await updateOrganization.mutateAsync({ inviteDomains });
                toast({
                    title: "Updated allowed email domains",
                });
            } catch (err) {
                toast({
                    title: "Failed to update organization's allowed email domains",
                    description: formatError(err),
                });
                throw err;
            }
        },
        [orgData, updateOrganization, toast],
    );

    const handleAddDomain = useCallback(
        async (newDomain: string) => {
            await doUpdateInviteDomains([...inviteDomains, newDomain]);
        },
        [doUpdateInviteDomains, inviteDomains],
    );

    const handleRemoveDomain = useCallback(
        async (removeDomain: string) => {
            await doUpdateInviteDomains(inviteDomains.filter((domain) => domain !== removeDomain));
        },
        [doUpdateInviteDomains, inviteDomains],
    );

    return (
        <div className="flex flex-col gap-3">
            <div>
                <Heading3>Allowed email domains</Heading3>
                <Subheading>Enter an email domain of an existing member of this organization.</Subheading>
            </div>

            <TagsInput
                tags={inviteDomains}
                validateInputFn={isValidDomain}
                onAddValue={handleAddDomain}
                onRemoveTag={handleRemoveDomain}
                placeHolder="example.com"
            />
        </div>
    );
};
